"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Hero() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      gsap.set([".hero-eyebrow", ".hero-word", ".hero-marker", ".hero-body", ".hero-cta", ".hero-image"], {
        opacity: 1,
        y: 0,
        scale: 1
      });
      gsap.set(".hero-marker-path", {
        strokeDashoffset: 0
      });
      return;
    }
    const tl = gsap.timeline({
      defaults: {
        ease: "power3.out"
      }
    });
    tl.from(".hero-eyebrow", {
      opacity: 0,
      y: 16,
      duration: 0.5
    }).from(".hero-word", {
      opacity: 0,
      y: 24,
      duration: 0.6,
      stagger: 0.05
    }, "-=0.2").from(".hero-marker", {
      opacity: 0,
      duration: 0.15
    }, "-=0.1").fromTo(".hero-marker-path", {
      strokeDashoffset: 300
    }, {
      strokeDashoffset: 0,
      duration: 0.7,
      ease: "power2.inOut"
    }, "-=0.05").from(".hero-body", {
      opacity: 0,
      y: 16,
      duration: 0.5
    }, "-=0.3").from(".hero-cta", {
      opacity: 0,
      y: 16,
      duration: 0.5,
      stagger: 0.1
    }, "-=0.3").from(".hero-image", {
      opacity: 0,
      scale: 0.96,
      y: 20,
      duration: 0.8
    }, "-=0.5");
  }, {
    scope: container
  });
  const headlineWords = ["Professional", "SEO", "Consultant", "for", "Sustainable", "Search"];
  return <section data-section-id="23020" id="professional-seo-consultant-for-sustainable-search-growth" className="bg-background text-foreground relative overflow-hidden py-16 md:py-24 lg:py-28 border-b border-border/60">
      <div ref={container} className="mx-auto w-full max-w-[1280px] px-5 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <div className="hero-eyebrow mb-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-secondary/80 border border-secondary text-primary font-sans text-xs font-semibold tracking-wider uppercase">
              <span className="w-2 h-2 rounded-lg bg-primary animate-pulse" />
              Yassine Essaadi &middot; Independent Freelance SEO Consultant
            </div>

            <h2 className="font-serif text-foreground font-extrabold tracking-tight mb-6" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
            lineHeight: 1.1
          }}>
              {headlineWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-visible mr-[0.25em]">
                  <span className="hero-word inline-block">{word}</span>
                </span>)}
              <span className="inline-block relative overflow-visible">
                <span className="hero-word inline-block text-primary font-extrabold">
                  Growth
                </span>
                <span className="hero-marker pointer-events-none absolute left-0 right-0" style={{
                bottom: "-0.08em",
                height: "0.18em"
              }} aria-hidden="true">
                  <svg viewBox="0 0 300 20" preserveAspectRatio="none" className="w-full h-full">
                    <path className="hero-marker-path" d="M4 12 C 40 6, 90 16, 150 10 S 260 8, 296 12" stroke="hsl(340, 75%, 65%)" strokeWidth="10" strokeLinecap="round" fill="none" style={{
                    strokeDasharray: 300,
                    strokeDashoffset: 0,
                    opacity: 0.9
                  }} />
                  </svg>
                </span>
              </span>
            </h2>

            <p className="hero-body font-sans text-muted-foreground text-lg md:text-xl leading-relaxed mb-8 max-w-2xl">
              I provide tailored search engine optimisation and related professional services for businesses seeking stronger organic visibility, qualified traffic and sustainable growth across UK, national and international markets.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <div className="hero-cta w-full sm:w-auto">
                <Button asChild className="w-full sm:w-auto rounded-lg h-auto px-7 py-3.5 text-base font-semibold bg-primary text-primary-foreground shadow-md hover:bg-primary/90 transition-all duration-200">
                  <Link to="/ContactPage">Hire Me Directly</Link>
                </Button>
              </div>
              <div className="hero-cta w-full sm:w-auto">
                <Button asChild variant="outline" className="w-full sm:w-auto rounded-lg h-auto px-7 py-3.5 text-base font-semibold border-foreground/20 text-foreground hover:bg-foreground hover:text-background transition-all duration-200">
                  <Link to="/SeoAuditsPage">Get a Free SEO Audit</Link>
                </Button>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-border/60 grid grid-cols-3 gap-4 w-full text-xs font-medium text-muted-foreground">
              <div>
                <span className="block text-foreground font-bold text-sm mb-0.5">Proven Expertise</span>
                Data-driven search growth
              </div>
              <div>
                <span className="block text-foreground font-bold text-sm mb-0.5">Tailored Strategy</span>
                Custom SEO roadmaps
              </div>
              <div>
                <span className="block text-foreground font-bold text-sm mb-0.5">National & Global</span>
                UK & International reach
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
            <div className="hero-image relative w-full max-w-md lg:max-w-none">
              <div className="absolute -top-10 -right-10 w-72 h-72 rounded-lg bg-primary/15 blur-3xl -z-10 pointer-events-none" />
              <div className="absolute -bottom-10 -left-10 w-80 h-80 rounded-lg bg-accent/25 blur-3xl -z-10 pointer-events-none" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-lg bg-secondary/70 blur-2xl -z-10 pointer-events-none" />

              <div className="relative rounded-2xl p-2.5 bg-card shadow-xl border border-border/80">
                <div className="overflow-hidden rounded-xl bg-muted/40 relative">
                  <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Independent Freelance SEO Consultant" loading="eager" className="w-full h-auto object-cover max-h-[520px] rounded-xl" src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" />

                  <div className="absolute bottom-4 left-4 right-4 bg-background/90 backdrop-blur-md p-3.5 rounded-lg border border-border/60 shadow-lg flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-foreground text-sm">Yassine Essaadi</p>
                      <p className="text-xs text-muted-foreground">Independent Freelance SEO Consultant</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
                      <span className="w-2 h-2 rounded-lg bg-emerald-500 animate-pulse" />
                      Available
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}